
import { supabase } from "@/integrations/supabase/client";
import { SharedList } from "@/types";
import logger from "@/utils/logger";
import { getCachedUserId, setCachedUser } from "@/services/auth/sessionCache";

// Function to share a list with another user
export const shareListWithUser = async (
  listId: string,
  userEmail: string
): Promise<true | false | "already_shared"> => {
  try {
    let userId = await getCachedUserId();
    let userSenderEmail: string | null = null;
    if (!userId) {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;
      setCachedUser(userData.user);
      userId = userData.user.id;
      userSenderEmail = userData.user.email;
    } else {
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        userSenderEmail = userData.user.email;
        setCachedUser(userData.user);
      }
    }

    if (!userId) return false;

    // Verifica possesso lista
    const { data: list, error: listError } = await supabase
      .from('shopping_lists')
      .select('*')
      .eq('id', listId)
      .eq('user_id', userId)
      .single();

    if (listError || !list) {
      console.error('Error retrieving list or user is not the owner:', listError);
      return false;
    }

    // Tentativo di inserimento (usa onConflict se possibile)
    const { error: insertError } = await supabase
      .from('shared_lists')
      .insert([
        {
          list_id: listId,
          owner_id: userId,
          shared_with_email: userEmail,
          status: 'pending'
        }
      ]);

    if (insertError && insertError.code === '23505') {
      // Codice 23505 = Unique violation
      logger.debug('Lista già condivisa (unique violation)');
      return "already_shared";
    }

    if (insertError) {
      console.error('Insert error:', insertError);
      return false;
    }

    // Recupero token sessione per invio e-mail
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) return false;
    const token = sessionData.session.access_token;
    const response = await fetch("https://gxnmcjgxredhctobhkki.supabase.co/functions/v1/send-invite-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        email: userEmail,
        listName: list.name || "Ceralia - Lista condivisa",
        inviter: userSenderEmail
      })
    });

    const resText = await response.text();
    logger.debug('Email response:', resText);

    return true;
  } catch (err) {
    console.error('General error sharing list:', err);
    return false;
  }
};


// Alias for backward compatibility
export const shareList = shareListWithUser;

// Function to get all shared lists for the current user
export const getSharedLists = async (): Promise<SharedList[]> => {
  try {
    let userEmail: string | null = null;
    const userId = await getCachedUserId();
    if (userId) {
      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        userEmail = userData.user.email;
        setCachedUser(userData.user);
      }
    } else {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return [];
      userEmail = userData.user.email;
      setCachedUser(userData.user);
    }

    if (!userEmail) return [];
    if (!userEmail) return [];
    
    // Get lists shared with the current user
    const { data: sharedListsData, error: sharedError } = await supabase
      .from('shared_lists')
      .select('*')
      .eq('shared_with_email', userEmail);
    
    if (sharedError) {
      console.error('Error retrieving shared lists:', sharedError);
      return [];
    }
    
    const sharedLists: SharedList[] = [];
    
    // Enhance each shared list with additional info
    for (const sharedList of sharedListsData || []) {
      try {
        // Get the list name
        const { data: listData } = await supabase
          .from('shopping_lists')
          .select('name')
          .eq('id', sharedList.list_id)
          .maybeSingle();
        
        // Get the owner info from profiles table using RLS-safe method
        const { data: ownerData } = await supabase
          .from('profiles')
          .select('name, email')
          .eq('id', sharedList.owner_id)
          .maybeSingle();
        
        sharedLists.push({
          id: sharedList.id,
          listId: sharedList.list_id,
          ownerId: sharedList.owner_id,
          sharedWithEmail: sharedList.shared_with_email,
          status: sharedList.status,
          createdAt: sharedList.created_at,
          updatedAt: sharedList.updated_at,
          listName: listData?.name || 'Lista senza nome',
          ownerName: ownerData?.name || 'Utente sconosciuto',
          ownerEmail: ownerData?.email || 'Email sconosciuta'
        });
      } catch (err) {
        console.error(`Error processing shared list ${sharedList.id}:`, err);
      }
    }
    
    return sharedLists;
  } catch (err) {
    console.error('General error retrieving shared lists:', err);
    return [];
  }
};

// Functions to accept/reject/delete shared lists with better error handling
export const acceptSharedList = async (sharedListId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('shared_lists')
      .update({ status: 'accepted' })
      .eq('id', sharedListId);
    
    if (error) {
      console.error(`Error accepting shared list ${sharedListId}:`, error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error(`General error accepting shared list ${sharedListId}:`, err);
    return false;
  }
};

export const rejectSharedList = async (sharedListId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('shared_lists')
      .update({ status: 'rejected' })
      .eq('id', sharedListId);
    
    if (error) {
      console.error(`Error rejecting shared list ${sharedListId}:`, error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error(`General error rejecting shared list ${sharedListId}:`, err);
    return false;
  }
};

export const deleteSharedList = async (sharedListId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('shared_lists')
      .delete()
      .eq('id', sharedListId);
    
    if (error) {
      console.error(`Error deleting shared list ${sharedListId}:`, error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error(`General error deleting shared list ${sharedListId}:`, err);
    return false;
  }
};
